import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Mic, 
  Brain, 
  Flame, 
  FileText, 
  ChevronRight,
  Check,
  Heart,
  Trophy,
  Volume2,
  Palette,
  Shield,
  Sparkles,
  Calendar,
  Sun,
  Zap,
  Search,
  BarChart3,
  Paintbrush,
  LogIn,
  User,
  Loader2
} from 'lucide-react';
import { usePresenceTracking } from '@/hooks/usePresenceTracking';

const API = process.env.REACT_APP_BACKEND_URL;

// Brand Colors
const COLORS = {
  pink: '#DF1283',
  orange: '#FF7901',
  lime: '#D0FF0B',
  cyan: '#0FFFE2',
  black: '#000000',
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(null); // 'monthly' or 'yearly'
  const [foundingMemberAvailable, setFoundingMemberAvailable] = useState(false); // Default to false until API confirms
  const [foundingMemberExpiry, setFoundingMemberExpiry] = useState(null);
  const [landingSettings, setLandingSettings] = useState(null);

  // Format expiration date for user display - hardcoded for reliability
  const formatUserFacingExpiration = () => {
    return 'Ends August 10, 2026 at midnight EST';
  };

  // Track presence on landing page
  usePresenceTracking('landing');

  // Fetch landing page settings
  useEffect(() => {
    const fetchLandingSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/public/landing-settings`);
        setLandingSettings(response.data.config);
      } catch (err) {
        console.error('Error fetching landing settings:', err);
        // Use defaults if fetch fails
        setLandingSettings({
          hero_button: {
            text: "Become a Founding Member Free",
            fallback_text: "Become a Member Today",
            action: "scroll_to_pricing"
          },
          secondary_button: {
            text: "Learn More",
            action: "scroll_to_features"
          },
          video: {
            enabled: true,
            youtube_url: "https://www.youtube.com/embed/YyiNzi0bciA?rel=0",
            label: "Watch Tutorial"
          }
        });
      }
    };
    fetchLandingSettings();
  }, []);

  // Handle hash navigation (e.g., /#pricing from redirect)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#pricing') {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, []);

  // Check if founding member is still available
  useEffect(() => {
    const checkFoundingMember = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/founding-member-status`);
        setFoundingMemberAvailable(response.data.available);
        if (response.data.expiration_date) {
          setFoundingMemberExpiry(new Date(response.data.expiration_date));
        }
      } catch (err) {
        // Default to available if check fails
        console.error('Error checking founding member status:', err);
      }
    };
    checkFoundingMember();
  }, []);

  const handleGetStarted = async (priceType) => {
    setCheckoutLoading(priceType);
    try {
      // Capture UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        utm_term: urlParams.get('utm_term'),
        utm_content: urlParams.get('utm_content'),
        ref: urlParams.get('ref')
      };
      
      // Remove null/undefined values
      const cleanUtmParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, v]) => v != null)
      );
      
      const response = await axios.post(`${API}/api/stripe/guest-checkout`, {
        price_type: priceType,
        ...cleanUtmParams
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const features = [
    {
      icon: Mic,
      title: "Voice Typing",
      description: "Speak your thoughts naturally. Powered by OpenAI Whisper for crystal-clear transcription across all your devices.",
      color: COLORS.cyan
    },
    {
      icon: Heart,
      title: "Mood Tracking",
      description: "Log daily emotions with 17+ granular emoji moods. Visualize your emotional patterns and understand yourself better.",
      color: COLORS.pink
    },
    {
      icon: Brain,
      title: "AI Journal Coach",
      description: "Get personalized reflections and insights powered by advanced AI. Your personal growth companion that learns from your entries.",
      color: COLORS.lime
    },
    {
      icon: Volume2,
      title: "Voice Playback",
      description: "Listen to your AI reflections read aloud in multiple natural-sounding voices. Perfect for on-the-go reflection.",
      color: COLORS.orange
    },
    {
      icon: Flame,
      title: "Streaks & Badges",
      description: "Build the journaling habit with gamified milestones. Track your progress, earn achievements, and stay motivated.",
      color: COLORS.orange
    },
    {
      icon: FileText,
      title: "PDF Export & Print",
      description: "Export beautifully formatted journals with your images and AI reflections included. Share or archive your journey.",
      color: COLORS.cyan
    },
    {
      icon: Palette,
      title: "Rich Text Editor",
      description: "Format entries with colors, emojis, and photos. Insert emojis directly from the toolbar and style your journal your way.",
      color: COLORS.pink
    },
    {
      icon: Calendar,
      title: "Daily Prompts",
      description: "Never stare at a blank page. Get thoughtful prompts and morning affirmations to inspire your writing.",
      color: COLORS.lime
    },
    {
      icon: Paintbrush,
      title: "Theme Customization",
      description: "Make it yours with Gradient or Flat modes, 8 stunning presets, custom colors, and emoji skin tone selection.",
      color: COLORS.cyan
    },
    {
      icon: BarChart3,
      title: "Mood Insights",
      description: "Visualize your emotional patterns with weekly and monthly analytics. Track trends and understand what drives your moods.",
      color: COLORS.lime
    },
    {
      icon: Search,
      title: "Journal Search",
      description: "Find any memory instantly with full-text search across all your entries. Never lose a thought again.",
      color: COLORS.orange
    },
    {
      icon: Sun,
      title: "Morning Affirmations",
      description: "Start each day with AI-generated affirmations personalized to your recent moods and journal themes.",
      color: COLORS.pink
    }
  ];

  const benefits = [
    { 
      icon: Zap,
      title: "Reduce Stress & Anxiety", 
      desc: "Studies show journaling decreases cortisol levels by up to 25%. Start each day with a clear mind.",
      color: COLORS.cyan
    },
    { 
      icon: Sun,
      title: "Build Self-Awareness", 
      desc: "Track patterns in your emotions and behaviors over time. Understand what makes you tick.",
      color: COLORS.lime
    },
    { 
      icon: Sparkles,
      title: "Boost Creativity", 
      desc: "Morning pages unlock creative potential that flows throughout your entire day.",
      color: COLORS.orange
    },
    { 
      icon: Brain,
      title: "Strengthen Memory", 
      desc: "Writing reinforces neural pathways, improving recall and cognitive function.",
      color: COLORS.pink
    },
    { 
      icon: Trophy,
      title: "Achieve Goals Faster", 
      desc: "People who journal are 42% more likely to achieve their goals. Make progress visible.",
      color: COLORS.orange
    }
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      
      {/* Simple Login Button - Top Right */}
      <div className="absolute top-6 right-6 z-50">
        <a 
          href="/login"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105 no-underline"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.pink}, ${COLORS.orange})`,
            boxShadow: `0 4px 20px ${COLORS.pink}40`
          }}
          data-testid="login-btn"
        >
          <LogIn className="w-4 h-4" />
          <span>Login</span>
        </a>
      </div>
      
      {/* Hero Section - Centered */}
      <section className="relative flex items-center justify-center py-20 md:py-28 px-4">
        {/* Background Effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#DF1283]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FF7901]/10 rounded-full blur-[150px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#0FFFE2]/5 rounded-full blur-[100px]" />
        
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          {/* Logo with animated glow effect */}
          <div className="mb-10">
            <style>{`
              @keyframes gradient-x {
                0%, 100% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
              }
              .animate-gradient-x {
                animation: gradient-x 3s ease infinite;
              }
            `}</style>
            <div 
              className="w-full h-28 md:h-32 lg:h-36 animate-gradient-x mx-auto max-w-md lg:max-w-lg"
              style={{
                background: `linear-gradient(90deg, ${COLORS.pink} 0%, ${COLORS.orange} 25%, ${COLORS.lime} 50%, ${COLORS.cyan} 75%, ${COLORS.pink} 100%)`,
                backgroundSize: '200% 100%',
                WebkitMaskImage: `url("/new-logo-homepage.png")`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: `url("/new-logo-homepage.png")`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                filter: `drop-shadow(0 0 30px ${COLORS.pink}80) drop-shadow(0 0 60px ${COLORS.orange}50)`,
              }}
              data-testid="hero-logo"
            />
          </div>

          {/* Tagline - Centered */}
          <p className="text-xl md:text-2xl text-white/80 mb-10 leading-relaxed" data-testid="hero-tagline">
            Your private journal with{' '}
            <span style={{ color: COLORS.cyan }} className="font-semibold">mood tracking</span>,{' '}
            <span style={{ color: COLORS.lime }} className="font-semibold">AI coaching</span>, and{' '}
            <span style={{ color: COLORS.orange }} className="font-semibold">voice typing</span>.
          </p>
          
          {/* CTAs - Centered - Only show after settings loaded to prevent flash */}
          {landingSettings && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <button 
                onClick={() => {
                  const action = landingSettings?.hero_button?.action || 'scroll_to_pricing';
                  if (action === 'external_link' && landingSettings?.hero_button?.external_url) {
                    window.open(landingSettings.hero_button.external_url, '_blank');
                  } else if (action === 'scroll_to_features') {
                    scrollToSection('features');
                  } else {
                    scrollToSection('pricing');
                  }
                }}
                className="group text-white px-8 py-4 rounded-full text-lg font-semibold transition-all shadow-xl flex items-center gap-2 hover:scale-105"
                style={{ 
                  background: `linear-gradient(90deg, #DF1283, #FF7901)`,
                  boxShadow: `0 10px 40px rgba(223, 18, 131, 0.4)`
                }}
                data-testid="hero-cta-primary"
              >
                <Trophy className="w-5 h-5" />
                {foundingMemberAvailable 
                  ? (landingSettings?.hero_button?.text || 'Become a Founding Member Free')
                  : (landingSettings?.hero_button?.fallback_text || 'Become a Member Today')
                }
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => {
                  const action = landingSettings?.secondary_button?.action || 'scroll_to_features';
                  if (action === 'external_link' && landingSettings?.secondary_button?.external_url) {
                    window.open(landingSettings.secondary_button.external_url, '_blank');
                  } else if (action === 'scroll_to_pricing') {
                    scrollToSection('pricing');
                  } else {
                    scrollToSection('features');
                  }
                }}
                className="text-white/70 hover:text-white px-8 py-4 text-lg transition-all flex items-center gap-2"
                data-testid="hero-cta-secondary"
              >
                {landingSettings?.secondary_button?.text || 'Learn More'}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
          
          {/* Loading placeholder for CTAs */}
          {!landingSettings && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <div className="h-14 w-72 rounded-full bg-white/10 animate-pulse" />
              <div className="h-14 w-32 rounded-full bg-white/5 animate-pulse" />
            </div>
          )}

          {/* Trust indicators - Centered */}
          <div className="flex items-center justify-center gap-6 text-sm text-white/50">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" style={{ color: COLORS.cyan }} />
              <span>Private & Secure</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: COLORS.lime }} />
              <span>AI Powered</span>
            </div>
          </div>

          {/* Video Tutorial Section - Only show if settings loaded AND video enabled */}
          {landingSettings && landingSettings.video?.enabled === true && (
            <div className="mt-16 relative">
              {/* Animated glow effect container */}
              <style>{`
                @keyframes video-glow {
                  0%, 100% { 
                    box-shadow: 0 0 40px ${COLORS.pink}60, 0 0 80px ${COLORS.orange}40, 0 0 120px ${COLORS.pink}20;
                  }
                  33% { 
                    box-shadow: 0 0 40px ${COLORS.orange}60, 0 0 80px ${COLORS.lime}40, 0 0 120px ${COLORS.orange}20;
                  }
                  66% { 
                    box-shadow: 0 0 40px ${COLORS.cyan}60, 0 0 80px ${COLORS.pink}40, 0 0 120px ${COLORS.cyan}20;
                  }
                }
                .video-glow-effect {
                  animation: video-glow 4s ease-in-out infinite;
                }
              `}</style>
              
              {/* Video container with glow */}
              <div 
                className="video-glow-effect overflow-hidden max-w-3xl mx-auto"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.pink}30, ${COLORS.orange}30, ${COLORS.cyan}30)`,
                  padding: '3px',
                }}
                data-testid="video-tutorial-container"
              >
                <div className="bg-[#0D0015] overflow-hidden">
                  <div 
                    className="relative w-full"
                    style={{ paddingBottom: '56.25%' }}
                  >
                    <iframe
                      src={landingSettings?.video?.youtube_url || "https://www.youtube.com/embed/YyiNzi0bciA?rel=0"}
                      title="UnCloned Journal Tutorial"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      data-testid="video-tutorial-iframe"
                    />
                  </div>
                </div>
              </div>
              
              {/* Video label */}
              <p className="text-center mt-4 text-sm text-white/50">
                {landingSettings?.video?.label || 'Watch a quick walkthrough of your journal'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Section Divider - Gradient Line */}
      <div className="w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${COLORS.pink}, ${COLORS.orange}, ${COLORS.lime}, ${COLORS.cyan}, transparent)` }} />

      {/* Features Section */}
      <section id="features" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 relative" style={{ background: 'linear-gradient(180deg, rgba(15,255,226,0.03) 0%, transparent 50%, rgba(208,255,11,0.03) 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: COLORS.orange }}>Features</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Everything You Need to{' '}
              <span style={{ 
                background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Journal Better</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              Powerful features designed to help you build a consistent journaling habit and unlock personal growth.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div 
                key={feature.title}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 group text-center"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform mx-auto"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                <p className="text-white/60 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${COLORS.cyan}, ${COLORS.lime}, transparent)` }} />

      {/* Benefits Section - Centered, No Images */}
      <section id="benefits" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: COLORS.cyan }}>Why Journal?</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              More Than a Journal.<br />
              <span style={{ 
                background: `linear-gradient(90deg, ${COLORS.lime}, ${COLORS.cyan})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>A Mental Sanctuary.</span>
            </h2>
          </div>
          
          {/* Benefits List - Centered */}
          <div className="space-y-6 max-w-2xl mx-auto">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="flex items-start gap-4 group">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all"
                  style={{ backgroundColor: `${benefit.color}15` }}
                >
                  <benefit.icon className="w-6 h-6" style={{ color: benefit.color }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1 text-white">{benefit.title}</h3>
                  <p className="text-white/60 leading-relaxed">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${COLORS.lime}, ${COLORS.orange}, transparent)` }} />

      {/* How It Works Section */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 relative" style={{ background: 'linear-gradient(180deg, rgba(255,121,1,0.03) 0%, transparent 50%, rgba(223,18,131,0.03) 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: COLORS.lime }}>How It Works</p>
            <h2 className="text-3xl md:text-4xl font-bold">
              Three Simple Steps to{' '}
              <span style={{ 
                background: `linear-gradient(90deg, ${COLORS.orange}, ${COLORS.pink})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Clarity</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Write or Speak", desc: "Type your thoughts or use voice typing to capture your morning reflections effortlessly.", color: COLORS.cyan },
              { step: "2", title: "Track Your Mood", desc: "Select from 17+ emoji moods to log how you're feeling. Watch patterns emerge over time.", color: COLORS.lime },
              { step: "3", title: "Get AI Insights", desc: "Receive personalized reflections and insights that help you understand yourself better.", color: COLORS.orange }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-black"
                  style={{ background: `linear-gradient(135deg, ${item.color}, ${COLORS.pink})` }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-white/60">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${COLORS.orange}, ${COLORS.pink}, transparent)` }} />

      {/* Testimonials Section - Masonry Style */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 relative">
        {/* Background Effects */}
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-[#DF1283]/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-[#0FFFE2]/8 rounded-full blur-[120px]" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: COLORS.cyan }}>Testimonials</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              What Are They{' '}
              <span style={{ 
                background: `linear-gradient(90deg, ${COLORS.lime}, ${COLORS.cyan})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Saying?</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              Real stories from journalers who transformed their mornings.
            </p>
          </div>

          {/* Masonry Grid - Alternating colors to prevent same-color stacking */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            
            {/* 1. Crystal Pope (Pink) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-crystal">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.pink}, ${COLORS.pink}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.pink}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/crystal-pope.jpg" alt="Crystal Pope"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-white/30 shadow-lg" />
                  <div>
                    <p className="font-bold text-white text-xl md:text-2xl">Crystal Pope</p>
                    <p className="text-base md:text-lg text-white/70">Founding Member</p>
                  </div>
                </div>
                <p className="text-white/95 leading-relaxed text-lg md:text-xl">
                  "Very cool! I really like the motivate feature. You've shared some great motivation videos in the past."
                </p>
              </div>
            </div>

            {/* 2. Halle Montgomery (Orange) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-halle">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.orange}, ${COLORS.orange}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.orange}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/halle-montgomery.jpg" alt="Halle Montgomery"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-white/30 shadow-lg"
                    style={{ objectPosition: 'center 20%' }} />
                  <div>
                    <p className="font-bold text-white text-xl md:text-2xl">Halle Montgomery</p>
                    <p className="text-base md:text-lg text-white/70">{foundingMemberAvailable ? 'Founding Member' : 'Early Member'}</p>
                  </div>
                </div>
                <p className="text-white/95 leading-relaxed text-lg md:text-xl">
                  "I love the customization and the motivation feature. The sound effect and 'surprise' were a great little moment of awe and wonder. The onboarding was smooth and easy, especially with the added 'sign up with google' feature. This is so pretty and everything is working smoothly, one of my qualms with Canva Code was the clunkiness of the previous iterations of projects, but this...this might truly change the game for solopreneurs around the world. I am so intrigued by this and I love the fact you learned a skill and implemented it so quickly. I wrote my first journal. I will definitely be adding this to my routine. Thank you for always creating awesome resources for us."
                </p>
              </div>
            </div>

            {/* 3. Shaquita Starks (Lime) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-shaquita">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.lime}, ${COLORS.lime}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.lime}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/shaquita-starks.jpg" alt="Shaquita Starks"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-black/20 shadow-lg" />
                  <div>
                    <p className="font-bold text-black text-xl md:text-2xl">Shaquita Starks</p>
                    <p className="text-base md:text-lg text-black/60">Founding Member</p>
                  </div>
                </div>
                <p className="text-black/90 leading-relaxed text-lg md:text-xl">
                  "Love it! Quick and easy to use and my adhd brain loves the flashy colors."
                </p>
              </div>
            </div>

            {/* 4. Dani Nicole (Cyan) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-dani">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.cyan}, ${COLORS.cyan}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.cyan}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/dani-nicole.jpg" alt="Dani Nicole"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-black/20 shadow-lg" />
                  <div>
                    <p className="font-bold text-black text-xl md:text-2xl">Dani Nicole</p>
                    <p className="text-base md:text-lg text-black/60">Founding Member</p>
                  </div>
                </div>
                <p className="text-black/90 leading-relaxed text-lg md:text-xl">
                  "I love everything about it. It is so well put together. Sometimes I don't feel like writing in my journal so having the option to voice it right from the top of my head almost like freestyle is great. I also like the prompt because sometimes I don't know what to write. And I like the reflection part so as soon as I'm done writing my entry, then the reflection kind of gives a synopsis of what I was feeling and articulates it for me cause sometimes I can't get the words out. Thank you for sharing this tool."
                </p>
              </div>
            </div>

            {/* 5. Renee Yvonne (Pink) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-renee">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.pink}, ${COLORS.pink}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.pink}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/renee-yvonne.jpg" alt="Renee Yvonne"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-white/30 shadow-lg" />
                  <div>
                    <p className="font-bold text-white text-xl md:text-2xl">Renee Yvonne</p>
                    <p className="text-base md:text-lg text-white/70">Founding Member</p>
                  </div>
                </div>
                <p className="text-white/95 leading-relaxed text-lg md:text-xl">
                  "Audria, as my granny would say, you are showing off! I love this! It's pretty, and I love the prompts. I used to love journaling, and then I got away from it. This may help me get back into it. What a wonderful an cool resource."
                </p>
              </div>
            </div>

            {/* 6. Charlene Brown (Orange) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-charlene">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.orange}, ${COLORS.orange}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.orange}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/charlene-brown.jpg" alt="Charlene Brown"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-white/30 shadow-lg" />
                  <div>
                    <p className="font-bold text-white text-xl md:text-2xl">Charlene Brown</p>
                    <p className="text-base md:text-lg text-white/70">Founding Member</p>
                  </div>
                </div>
                <p className="text-white/95 leading-relaxed text-lg md:text-xl">
                  "This is one of the best, well thought out journal apps I've ever had. If you journal and you cherish your privacy, the UnCloned® Journal is the app to use."
                </p>
              </div>
            </div>

            {/* 7. Vickia Johnson (Lime) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-vickia">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.lime}, ${COLORS.lime}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.lime}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/vickia-johnson.jpg" alt="Vickia Johnson"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-black/20 shadow-lg" />
                  <div>
                    <p className="font-bold text-black text-xl md:text-2xl">Vickia Johnson</p>
                    <p className="text-base md:text-lg text-black/60">Founding Member</p>
                  </div>
                </div>
                <p className="text-black/90 leading-relaxed text-lg md:text-xl">
                  "This is the journal of all digital journals!! It's not just the outside that's cool but the inside is just as fun! It screams Write in Me!! It's the privacy you want, the motivation you need, and the fun you have all in one!! Need somewhere to write your thoughts the UnCloned® Journal can be your best friend."
                </p>
              </div>
            </div>

            {/* 8. Dr. Katina Fuller-Scott (Cyan) */}
            <div className="break-inside-avoid group" data-testid="testimonial-card-katina">
              <div 
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                style={{ 
                  background: `linear-gradient(145deg, ${COLORS.cyan}, ${COLORS.cyan}dd)`,
                  boxShadow: `0 10px 40px ${COLORS.cyan}30`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src="/testimonials/katina-fuller-scott.jpg" alt="Dr. Katina Fuller-Scott"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-black/20 shadow-lg" />
                  <div>
                    <p className="font-bold text-black text-xl md:text-2xl">Dr. Katina Fuller-Scott</p>
                    <p className="text-base md:text-lg text-black/60">Founding Member</p>
                  </div>
                </div>
                <p className="text-black/90 leading-relaxed text-lg md:text-xl">
                  "Love love love it! On brand, extremely custom mixed experience! Great job Coach"
                </p>
              </div>
            </div>

          </div>

          {/* Trust indicator */}
          <div className="text-center mt-12">
            <p className="text-sm text-white/40 flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4" style={{ color: '#FFD700' }} />
              Join our growing community of Founding Members
            </p>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${COLORS.lime}, ${COLORS.orange}, transparent)` }} />

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: COLORS.pink }}>Pricing</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Simple,{' '}
              <span style={{ 
                background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Transparent</span> Pricing
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              Choose your plan and start journaling today.
            </p>
          </div>

          {/* Pricing Cards - 3 columns (or 2 if founding member expired) */}
          <div className={`grid gap-6 max-w-5xl mx-auto ${foundingMemberAvailable ? 'md:grid-cols-3' : 'md:grid-cols-2 max-w-3xl'}`}>
            
            {/* Founding Member - FREE (Limited Time) - Only show if available */}
            {foundingMemberAvailable && (
            <div 
              className="relative rounded-2xl p-8 transition-all duration-300"
              style={{ 
                background: `linear-gradient(135deg, #C5A54720, #FFD70015)`,
                border: `2px solid #C5A547`
              }}
              data-testid="pricing-founding"
            >
              {/* Founding Member Badge */}
              <div 
                className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold text-black flex items-center gap-2"
                style={{ background: `linear-gradient(90deg, #C5A547, #FFD700)` }}
              >
                <Trophy className="w-4 h-4" />
                Limited Time
              </div>
              
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#FFD700' }}>Founding Member</h3>
              <p className="text-white/50 text-sm mb-6">Exclusive early access</p>
              
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-bold" style={{ color: '#FFD700' }}>FREE</span>
              </div>
              <p className="text-sm mb-6 text-red-400 font-medium">
                {formatUserFacingExpiration()}
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span><strong className="text-white">Unlimited</strong> journal entries</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span><strong className="text-white">Unlimited</strong> voice typing</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span><strong className="text-white">Unlimited</strong> AI Coach reflections</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span>Listen to reflections read aloud</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span>17+ mood options with insights</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span>Customize your journal with your favorite colors</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span>Streak tracking & 8 badges to earn</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span>PDF export & printing</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span>Daily prompts & affirmations</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#FFD700' }} />
                  <span className="font-semibold" style={{ color: '#FFD700' }}>Exclusive Founding Member badge</span>
                </li>
              </ul>
              
              <button 
                onClick={() => navigate('/founding-member')}
                className="w-full py-4 rounded-full font-semibold transition-all text-black"
                style={{ 
                  background: `linear-gradient(90deg, #C5A547, #FFD700)`,
                  boxShadow: `0 10px 30px #C5A54730`
                }}
                data-testid="pricing-founding-cta"
              >
                Become a Founding Member
              </button>
            </div>
            )}

            {/* Monthly Plan */}
            <div 
              className="relative bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all duration-300"
              data-testid="pricing-monthly"
            >
              <h3 className="text-xl font-semibold mb-2">Monthly</h3>
              <p className="text-white/50 text-sm mb-6">Cancel anytime</p>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-5xl font-bold" style={{ color: COLORS.cyan }}>$9.99</span>
                <span className="text-white/50">/month</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span><strong className="text-white">Unlimited</strong> journal entries</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span><strong className="text-white">Unlimited</strong> voice typing</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span><strong className="text-white">Unlimited</strong> AI Coach reflections</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span>Listen to reflections read aloud</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span>17+ mood options with insights</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span>Customize your journal with your favorite colors</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span>Streak tracking & 8 badges to earn</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span>PDF export & printing</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.cyan }} />
                  <span>Daily prompts & affirmations</span>
                </li>
              </ul>
              
              <button 
                onClick={() => handleGetStarted('monthly')}
                disabled={checkoutLoading === 'monthly'}
                className="w-full py-4 rounded-full font-semibold transition-all border border-white/20 hover:border-white/40 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pricing-monthly-cta"
              >
                {checkoutLoading === 'monthly' ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  'Get Started'
                )}
              </button>
            </div>

            {/* Yearly Plan - Highlighted */}
            <div 
              className="relative rounded-2xl p-8 transition-all duration-300"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.pink}15, ${COLORS.orange}15)`,
                border: `2px solid ${COLORS.pink}50`
              }}
              data-testid="pricing-yearly"
            >
              {/* Best Value Badge */}
              <div 
                className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold text-black"
                style={{ background: `linear-gradient(90deg, ${COLORS.lime}, ${COLORS.cyan})` }}
              >
                Save 37%
              </div>
              
              <h3 className="text-xl font-semibold mb-2">Yearly</h3>
              <p className="text-white/50 text-sm mb-6">Best value</p>
              
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-bold" style={{ 
                  background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>$75</span>
                <span className="text-white/50">/year</span>
              </div>
              <p className="text-sm mb-6" style={{ color: COLORS.lime }}>
                That's only $6.25/month
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span><strong className="text-white">Unlimited</strong> journal entries</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span><strong className="text-white">Unlimited</strong> voice typing</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span><strong className="text-white">Unlimited</strong> AI Coach reflections</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span>Listen to reflections read aloud</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span>17+ mood options with insights</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span>Customize your journal with your favorite colors</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span>Streak tracking & 8 badges to earn</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span>PDF export & printing</span>
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.pink }} />
                  <span>Daily prompts & affirmations</span>
                </li>
              </ul>
              
              <button 
                onClick={() => handleGetStarted('yearly')}
                disabled={checkoutLoading === 'yearly'}
                className="w-full py-4 rounded-full font-semibold transition-all text-black disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
                  boxShadow: `0 10px 30px ${COLORS.pink}30`
                }}
                data-testid="pricing-yearly-cta"
              >
                {checkoutLoading === 'yearly' ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  'Get Started & Save'
                )}
              </button>
            </div>
          </div>

          <p className="text-center text-white/40 text-sm mt-10">
            Cancel anytime • Secure payment via Stripe
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${COLORS.lime}, ${COLORS.cyan}, transparent)` }} />

      {/* Final CTA */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Animated border */}
            <div 
              className="absolute -inset-[2px] rounded-2xl opacity-70"
              style={{ background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange}, ${COLORS.lime}, ${COLORS.cyan}, ${COLORS.pink})` }}
            />
            
            <div className="relative bg-black rounded-2xl p-10 md:p-16 text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-6" style={{ color: COLORS.lime }} />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Transform Your{' '}
                <span style={{ 
                  background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Life?</span>
              </h2>
              <p className="text-lg text-white/60 max-w-xl mx-auto mb-10">
                Join the journaling revolution. Start writing, start growing, start becoming the best version of yourself.
              </p>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="group text-black px-10 py-5 rounded-full text-lg font-semibold transition-all flex items-center gap-3 mx-auto"
                style={{ 
                  background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
                  boxShadow: `0 10px 40px ${COLORS.pink}40`
                }}
                data-testid="final-cta-button"
              >
                Start Journaling Today
                <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-sm text-white/40 mt-6">
                {foundingMemberAvailable ? (
                  <>
                    <span className="text-[#0FFFE2]">Founding Member</span> • {formatUserFacingExpiration()}
                  </>
                ) : (
                  <span className="text-[#0FFFE2]">Start your journaling journey today</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div 
              className="w-full h-10 animate-gradient-x"
              style={{
                background: `linear-gradient(90deg, ${COLORS.pink} 0%, ${COLORS.orange} 25%, ${COLORS.lime} 50%, ${COLORS.cyan} 75%, ${COLORS.pink} 100%)`,
                backgroundSize: '200% 100%',
                WebkitMaskImage: `url("/new-logo-homepage.png")`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'left center',
                maskImage: `url("/new-logo-homepage.png")`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'left center',
                maxWidth: '200px',
                opacity: 0.7
              }}
            />
            
            <div className="flex items-center gap-6 text-sm text-white/50">
              <button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Pricing</button>
              <button onClick={() => scrollToSection('benefits')} className="hover:text-white transition-colors">Benefits</button>
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Sign In</button>
              <Link to="/privacy" className="hover:text-[#0FFFE2] transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#0FFFE2] transition-colors">Terms of Service</Link>
            </div>
            
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} UnCloned® Media. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
